import { App as CapacitorApp } from '@capacitor/app'
import { Network } from '@capacitor/network'
import { getApiEndpoints } from '@/data/api/remote-api'
import { isNativePlatform } from '@/platform/platform'
import { installPendingOperationMirror, reconcilePendingOperationMirror } from '@/platform/pending-mirror'
import { nativeSync } from '@/platform/native-sync'
import { updateManager } from '@/platform/update-manager'
import { ensureLocalIdentity } from '@/services/identity/local-identity'
import { startSyncEngine } from '@/services/sync/sync-engine'

async function startNativeServices() {
  if (!isNativePlatform()) return
  installPendingOperationMirror()
  const identity = await ensureLocalIdentity()
  if (identity.authenticated) {
    const [endpoint, fallbackEndpoint] = getApiEndpoints()
    await nativeSync.configure({
      endpoint: endpoint || '',
      fallbackEndpoint: fallbackEndpoint || '',
      workspaceId: identity.workspaceId,
      deviceId: identity.deviceId,
    })
    await reconcilePendingOperationMirror(identity.workspaceId)
  }
  // The bundle survived startup; end the OTA probation window so the updater
  // will not roll back to the previous version on next launch.
  window.setTimeout(() => void updateManager.markHealthy(), 3000)
}

async function registerBackButton(disposers: Array<() => void>) {
  if (!isNativePlatform()) return
  const handle = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back()
    else void CapacitorApp.minimizeApp()
  })
  disposers.push(() => void handle.remove())
}

export async function startLifecycleServices() {
  await ensureLocalIdentity()
  const disposers: Array<() => void> = []
  disposers.push(startSyncEngine())
  const online = () => window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
  window.addEventListener('online', online)
  window.addEventListener('study-tool:local-change', online)
  disposers.push(() => window.removeEventListener('online', online))
  disposers.push(() => window.removeEventListener('study-tool:local-change', online))

  const networkHandle = await Network.addListener('networkStatusChange', status => {
    if (status.connected) online()
  })
  const appHandle = await CapacitorApp.addListener('appStateChange', state => {
    if (state.isActive) online()
  })
  disposers.push(() => void networkHandle.remove())
  disposers.push(() => void appHandle.remove())

  await registerBackButton(disposers)
  await startNativeServices().catch(error => console.warn('Native services init failed', error))

  return () => disposers.forEach(dispose => dispose())
}
